/**
 * Academy sold online in every country (D-110): shared rules of the web and mobile apps.
 * Countries, time zone, indicative prices (the reference price stays in XAF), payment method
 * texts, renewal stage, campaign parameters. Nothing here is a price: prices come from the API.
 */

import type { CountryCode } from "libphonenumber-js";

import { COUNTRIES } from "./countries";
import { formatXaf } from "./money";
import { toE164 } from "./phone";

export { COUNTRIES };

export type SalesText = Record<string, string> | string | null | undefined;

/** A text of the data in the reader's language (French when missing). */
export function localText(value: SalesText, language: string): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  const short = language.split("-")[0] ?? language;
  return value[short] ?? value.fr ?? value.en ?? Object.values(value)[0] ?? "";
}

const BY_CODE = new Map(COUNTRIES.map(([code, fr, en]) => [code, { fr, en }]));

export function isCountry(code: string): boolean {
  return BY_CODE.has(code.toUpperCase());
}

/** The country's name: French and English from the shared list, other languages from the
 * device (Intl.DisplayNames) when it knows them, English otherwise. */
export function countryName(code: string, language: string): string {
  const upper = code.toUpperCase();
  const row = BY_CODE.get(upper);
  const short = language.split("-")[0] ?? language;
  if (row && (short === "fr" || short === "en")) return row[short];
  try {
    const names = new Intl.DisplayNames([short], { type: "region" });
    const name = names.of(upper);
    if (name && name !== upper) return name;
  } catch {
    /* no Intl.DisplayNames (older engines): English below */
  }
  return row?.en ?? upper;
}

/** Every country as select options, sorted by name in the reader's language. */
export function countryOptions(language: string): { value: string; label: string }[] {
  const short = language.split("-")[0] ?? language;
  return COUNTRIES.map(([code]) => ({ value: code, label: countryName(code, short) })).sort((a, b) =>
    a.label.localeCompare(b.label, short),
  );
}

/** Time zones of the countries the agency serves first (the others use the locale's region). */
const ZONE_COUNTRY: Record<string, string> = {
  "Africa/Douala": "CM",
  "Africa/Abidjan": "CI",
  "Africa/Dakar": "SN",
  "Africa/Ouagadougou": "BF",
  "Africa/Bamako": "ML",
  "Africa/Lome": "TG",
  "Africa/Porto-Novo": "BJ",
  "Africa/Niamey": "NE",
  "Africa/Libreville": "GA",
  "Africa/Brazzaville": "CG",
  "Africa/Kinshasa": "CD",
  "Africa/Lubumbashi": "CD",
  "Africa/Conakry": "GN",
  "Africa/Ndjamena": "TD",
  "Africa/Bangui": "CF",
  "Africa/Malabo": "GQ",
  "Africa/Casablanca": "MA",
  "Africa/Tunis": "TN",
  "Africa/Algiers": "DZ",
  "Indian/Antananarivo": "MG",
  "Europe/Paris": "FR",
  "Europe/Brussels": "BE",
  "Europe/Zurich": "CH",
  "Europe/Luxembourg": "LU",
  "America/Toronto": "CA",
  "America/Montreal": "CA",
  "America/Vancouver": "CA",
  "America/Edmonton": "CA",
  "America/Winnipeg": "CA",
  "America/Halifax": "CA",
  "America/Moncton": "CA",
  "America/Regina": "CA",
};

/** The device's IANA time zone ("" when unknown). */
export function detectTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
  } catch {
    return "";
  }
}

/** A first guess of the country of residence (the learner confirms it). */
export function guessCountry(timeZone: string, locales: readonly string[] = []): string {
  const byZone = ZONE_COUNTRY[timeZone];
  if (byZone) return byZone;
  for (const locale of locales) {
    const region = locale.split(/[-_]/)[1]?.toUpperCase();
    if (region && region.length === 2 && isCountry(region)) return region;
  }
  return "";
}

/** EU and EEA consumers: 14-day right of withdrawal for digital content (terms, section 5). */
export const EU_COUNTRIES: readonly string[] = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV",
  "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "IS", "LI", "NO",
  "RE", "GP", "MQ", "GF", "YT",
];

export function isEuConsumer(country: string): boolean {
  return EU_COUNTRIES.includes(country.toUpperCase());
}

/** A phone number of the learner's country, in E.164 (+221…), or null when invalid. */
export function phoneFor(raw: string, country: string): string | null {
  const region = (isCountry(country) ? country.toUpperCase() : "CM") as CountryCode;
  return toE164(raw, region);
}

// --- Prices ------------------------------------------------------------------------------------

export interface FxRate {
  xaf_per_unit: number;
  fixed?: boolean;
  decimals?: number;
}

export interface FxTable {
  rates?: Record<string, FxRate>;
  cfa_countries?: Record<string, string[]>;
  display_by_country?: Record<string, string>;
  default_display?: string;
  as_of?: string;
}

/** Is the country's currency a franc CFA (the price is already in its currency)? */
export function isCfaCountry(country: string, fx: FxTable): boolean {
  return Object.values(fx.cfa_countries ?? {}).some((codes) => codes.includes(country.toUpperCase()));
}

/** The currency shown next to the XAF price (none in the CFA countries). */
export function displayCurrency(country: string, fx: FxTable): string | null {
  if (country && isCfaCountry(country, fx)) return null;
  const wanted = (country && fx.display_by_country?.[country.toUpperCase()]) || fx.default_display || "EUR";
  return fx.rates?.[wanted] ? wanted : null;
}

export function convertFromXaf(xaf: number, currency: string, fx: FxTable): number | null {
  const rate = fx.rates?.[currency.toUpperCase()];
  if (!rate || !(rate.xaf_per_unit > 0)) return null;
  return xaf / rate.xaf_per_unit;
}

/** « 15,24 € », « CA$ 24.69 »… in the reader's language. */
export function formatMoney(amount: number, currency: string, locale: string): string {
  const upper = currency.toUpperCase();
  if (upper === "XAF" || upper === "XOF") return formatXaf(Math.round(amount));
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: upper }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${upper}`;
  }
}

export interface IndicativePrice {
  currency: string;
  text: string;
  /** fixed official parity (EUR, XOF) or an indicative rate that changes (CAD, USD) */
  fixed: boolean;
}

/** The XAF price in the learner's currency, for information only (always charged in XAF, or in
 * the currency of the payment page). */
export function indicativePrice(xaf: number, country: string, fx: FxTable, locale: string): IndicativePrice | null {
  const currency = displayCurrency(country, fx);
  if (!currency) return null;
  const amount = convertFromXaf(xaf, currency, fx);
  if (amount === null) return null;
  return { currency, text: formatMoney(amount, currency, locale), fixed: Boolean(fx.rates?.[currency]?.fixed) };
}

/** What the payment page charges: francs CFA as « 10 000 FCFA », euros from cents. */
export function chargedText(amountMinor: number, currency: string, locale: string): string {
  const upper = currency.toUpperCase();
  if (upper === "XAF" || upper === "XOF") return formatXaf(amountMinor);
  return formatMoney(amountMinor / 100, upper, locale);
}

// --- Access and renewal ---------------------------------------------------------------------------

/** Whole days from `today` to `until` (a « YYYY-MM-DD » date, inclusive end of access). */
export function daysLeft(until: string, today: Date = new Date()): number {
  const [y, m, d] = until.split("-").map(Number);
  const end = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((end - now) / 86_400_000);
}

export type RenewalStage = "none" | "active" | "soon" | "last" | "expired";

/** « soon »: 3 days or less left; « last »: the last day; « expired »: a past plan, none now. */
export function renewalStage(activeUntil: string | null | undefined, hadPlan: boolean, today: Date = new Date()): RenewalStage {
  if (!activeUntil) return hadPlan ? "expired" : "none";
  const left = daysLeft(activeUntil, today);
  if (left < 0) return "expired";
  if (left === 0) return "last";
  if (left <= 3) return "soon";
  return "active";
}

// --- Campaigns ----------------------------------------------------------------------------------

export const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

/** The campaign parameters of a landing address (and the referral code « ref »). */
export function campaignFrom(search: string): { utm: Record<string, string>; referral: string } {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const utm: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) utm[key] = value.slice(0, 80);
  }
  const referral = (params.get("ref") ?? "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16);
  return { utm, referral };
}

// --- Reading the JSON parts of the sale API (typed once for web and mobile) --------------------

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

export function asFx(value: unknown): FxTable {
  return record(value) as FxTable;
}

export interface TrialOffer {
  placement: boolean;
  method: boolean;
  discovery: boolean;
  discovery_questions: number;
  days: number;
}

/** D-172: what the free mock contains, as the server sends it (read from the mock and the formats). */
export interface FreeMockSection {
  skill: string;
  minutes: number;
  questions: number;
  tasks: number;
}

export interface FreeMockContent {
  id: string;
  number: number;
  title: string;
  minutes: number;
  sections: FreeMockSection[];
}

export function asFreeMockContent(value: unknown): FreeMockContent | null {
  const row = record(value);
  if (!row.id) return null;
  const sections = Array.isArray(row.sections) ? row.sections : [];
  return {
    id: String(row.id),
    number: Number(row.number ?? 0),
    title: String(row.title ?? ""),
    minutes: Number(row.minutes ?? 0),
    sections: sections.map((item) => {
      const section = record(item);
      return {
        skill: String(section.skill ?? ""),
        minutes: Number(section.minutes ?? 0),
        questions: Number(section.questions ?? 0),
        tasks: Number(section.tasks ?? 0),
      };
    }),
  };
}

export function asTrialOffer(value: unknown): TrialOffer {
  const row = record(value);
  return {
    placement: row.placement !== false,
    method: Boolean(row.method),
    discovery: Boolean(row.discovery),
    discovery_questions: Number(row.discovery_questions ?? 10),
    days: Number(row.days ?? 0),
  };
}

export interface TrialStatus {
  discovery_enabled: boolean;
  discovery_questions: number;
  discovery_used: Record<string, boolean>;
  days: number;
  trial_until: string | null;
}

export function asTrialStatus(value: unknown): TrialStatus {
  const row = record(value);
  return {
    discovery_enabled: Boolean(row.discovery_enabled),
    discovery_questions: Number(row.discovery_questions ?? 10),
    discovery_used: record(row.discovery_used) as Record<string, boolean>,
    days: Number(row.days ?? 0),
    trial_until: typeof row.trial_until === "string" ? row.trial_until : null,
  };
}

export function asTexts(value: unknown): SalesText[] {
  return Array.isArray(value) ? (value as SalesText[]) : [];
}

export interface ReferralInfo {
  enabled: boolean;
  code: string;
  count: number;
  referee_discount_percent: number;
  referrer_free_days: number;
}

export function asReferral(value: unknown): ReferralInfo {
  const row = record(value);
  return {
    enabled: Boolean(row.enabled),
    code: String(row.code ?? ""),
    count: Number(row.count ?? 0),
    referee_discount_percent: Number(row.referee_discount_percent ?? 0),
    referrer_free_days: Number(row.referrer_free_days ?? 0),
  };
}

export type MobilePurchase = "web_link" | "none";

/** How the mobile app may sell (store rules, D-110): a link to the website, or information only. */
export function mobilePurchase(value: unknown, platform: string): MobilePurchase {
  const row = record(value);
  return row[platform] === "web_link" ? "web_link" : "none";
}

export interface SubscriptionRow {
  id: string;
  kind: string;
  status: string;
  starts_on: string | null;
  ends_on: string | null;
  amount_xaf: number;
  method: string;
  invoice_number: string;
  invoice_url: string | null;
}

export function asSubscriptions(value: unknown): SubscriptionRow[] {
  return Array.isArray(value) ? (value as SubscriptionRow[]) : [];
}

export interface Consents {
  analytics: boolean;
  marketing: boolean;
}

export function asConsents(value: unknown): Consents {
  const row = record(value);
  const flag = (key: string) => Boolean(record(row[key]).value);
  return { analytics: flag("analytics"), marketing: flag("marketing") };
}
